import React, { useCallback, useEffect, useState } from 'react';
import { zielFuer, zeilenKlick } from './ziel';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, FolderOpen, Users, Link2, Package } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { useMerkZustand } from '../../hooks/useMerkZustand';
import { NutzerPanel } from './NutzerPanel';
import { ZuordnenDialog } from './ZuordnenDialog';
import { Zeitstrahl } from './Zeitstrahl';
import { StammdatenKarte, AkteBearbeiten, statusLabel } from './StammdatenKarte';
import { ZahlungenKarte } from './ZahlungenKarte';
import { AkontoLinkDialog } from './AkontoLinkDialog';
import { Stammblatt } from './Stammblatt';
import { Zugriffe } from './MandatKarten';
import { KopfKarte } from './KopfKarte';
import { VerbundTag } from './VerbundTag';
import { VerbundVorschlaege } from './VerbundVorschlaege';
import { RechnungenKarte } from './RechnungenKarte';
import { ErsteAkteButton } from './ErsteAkteButton';
import { ZeilenAktionen } from './ZeilenAktionen';
import { MandantChat } from './MandantChat';
import { SignaturHaekchen } from '../signaturen/SignaturHaekchen';
import { ErweiterteStammdaten } from '../stammakte/ErweiterteStammdaten';
import { AkteExtras, Randnotizen } from '../stammakte/AkteExtras';
import { AnlageHerkunft } from '../kanzlei/AnlageHerkunft';
import { HonorarKarte, AkontoOhneRechnung } from '../stammakte/HonorarKarte';
import { AblageStreifen } from '../kanzlei/AblageStreifen';

const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return iso; } };

const Block = ({ title, icon: Icon, count, children, testId, className = '', id }) => (
  <section id={id} className={`bg-white border border-gray-200 rounded-2xl scroll-mt-48 ${className}`} data-testid={testId}>
    <header className="flex items-center gap-2.5 px-4 py-2 border-b border-gray-100">
      {Icon && <span className="w-6 h-6 rounded-lg bg-[#b8893d]/10 flex items-center justify-center"><Icon className="w-3.5 h-3.5 text-[#b8893d]" /></span>}
      <h3 className="font-serif text-lg text-[#1B3A5C]">{title}</h3>
      {count != null && <span className="ml-auto min-w-[1.75rem] text-center px-2 py-0.5 rounded-full bg-[#f5f8fa] text-[11px] font-mono text-[#34536b]">{count}</span>}
    </header>
    <div className="p-3">{children}</div>
  </section>
);

const REITER = [['uebersicht', 'Übersicht'], ['akten', 'Akten'], ['nachrichten', 'Nachrichten'], ['dokumente', 'Dokumente'], ['finanzen', 'Finanzen'], ['stammdaten', 'Stammdaten'], ['zeitstrahl', 'Zeitstrahl'], ['zugriffe', 'Zugriffe']];
const Reiter = ({ an, setAn, zahlen }) => (
  <nav className="sticky top-[155px] lg:top-[128px] z-20 -mx-1 px-1 py-1.5 bg-[#f5f8fa]/90 backdrop-blur-md" aria-label="Bereiche des Mandanten" data-testid="m360-ansicht-tabs">
    <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-[#1B3A5C]/10">
      {REITER.map(([k, l]) => (
        <button key={k} type="button" onClick={() => { setAn(k); const kopf = document.querySelector('[data-testid="m360-kopf"]'); if (kopf && kopf.getBoundingClientRect().bottom < 0) kopf.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} aria-current={an === k ? 'page' : undefined} data-testid={`m360-tab-${k}`}
          className={`relative shrink-0 whitespace-nowrap px-4 py-2.5 text-[13px] font-medium transition-colors duration-200 ${an === k ? 'text-[#1B3A5C]' : 'text-[#7a8a99] hover:text-[#1B3A5C]'}`}>
          {l}{zahlen[k] != null && <span className={`ml-1.5 inline-flex min-w-[1.25rem] justify-center px-1.5 rounded-full text-[10px] font-mono ${an === k ? 'bg-[#1B3A5C] text-white' : 'bg-[#1B3A5C]/[0.07] text-[#34536b]'}`}>{zahlen[k]}</span>}
          <span className={`absolute left-2 right-2 -bottom-px h-0.5 rounded-full transition-colors ${an === k ? 'bg-[#b8893d]' : 'bg-transparent'}`} />
        </button>
      ))}
    </div>
  </nav>
);

const Leer = ({ text, testId }) => <p className="px-1 py-6 text-center text-sm text-[#7a8a99]" data-testid={testId}>{text}</p>;


export const MappeBtn = ({ akte }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const run = async () => {
    setBusy(true); setErr(false);
    try {
      const r = await api.get(`/register/akten/${akte.case_id}/mappe.zip`, { responseType: 'blob', timeout: 120000 });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a'); a.href = url; a.download = `Uebergabe_${(akte.aktenzeichen || akte.case_id).replace(/[^A-Za-z0-9_-]+/g, '-')}.zip`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setErr(true); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={run} disabled={busy} data-testid={`m360-mappe-${akte.case_id}`} title="Übergabe-Mappe (ZIP): Zeitstrahl, Chat-Protokoll und alle Dokumente dieser Akte"
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wider border rounded-full transition-colors ${err ? 'border-rose-300 text-rose-700' : 'border-[#b8893d]/50 text-[#b8893d] hover:bg-[#b8893d] hover:text-white'}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Package className="w-3 h-3" />} {err ? 'FEHLER' : 'MAPPE'}
    </button>
  );
};

const AkteStatusBtn = ({ a, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const zu = a.status === 'abgeschlossen' || a.archived;
  const run = async () => {
    if (!window.confirm(zu ? 'Akte wieder öffnen?' : 'Akte abschließen? Sie bleibt beim Mandanten lesbar.')) return;
    setBusy(true);
    try { await api.patch(`/cases/${a.case_id}`, { status: zu ? 'in_bearbeitung' : 'abgeschlossen' }); onSaved(); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy} data-testid={`m360-akte-${zu ? 'oeffnen' : 'abschliessen'}-${a.case_id}`}
      className="px-2 py-0.5 text-[10px] tracking-wider border rounded-full border-[#1B3A5C]/30 text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white transition-colors">
      {busy ? '…' : zu ? 'WIEDER ÖFFNEN' : 'ABSCHLIESSEN'}
    </button>
  );
};

const ItemRow = ({ it, onEdit, mnr, onDone }) => {
  const navigate = useNavigate();
  return (
  <li onClick={zeilenKlick(navigate, zielFuer(it.area, it.id, it.verbund_nr || mnr, it.zuordnung?.case_id))} className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-sm rounded-lg hover:bg-[#f5f8fa] group cursor-pointer" data-testid={`reg-item-${it.area}-${it.id}`}>
    <VerbundTag nr={it.verbund_nr} />
    <span className="flex-1 min-w-[12rem] truncate text-[#1B3A5C]" title={it.titel}>{it.titel}{it.area === 'signaturen' && it.sub && <span className="ml-2 text-[10px] text-[#7a8a99]" data-testid={`reg-item-von-${it.id}`}>angefordert von {it.sub}</span>}</span>
    {it.zuordnung?.aktenzeichen && <span className="whitespace-nowrap font-mono text-[10px] text-[#34536b] bg-[#f5f8fa] px-1.5 py-0.5">AZ {it.zuordnung.aktenzeichen}</span>}
    {it.status && <span className="text-[10px] text-[#7a8a99]">{it.status}</span>}
    <span className="text-[10px] text-[#7a8a99] w-20 text-right">{fmt(it.datum)}</span>
    {it.haekchen && it.status !== 'storniert' && <SignaturHaekchen h={it.haekchen} status={it.status} id={it.id} />}
    <ZeilenAktionen it={it} onDone={onDone} />
    <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(it); }} title="Zuordnung ändern" data-testid={`reg-item-edit-${it.id}`} className="p-1 text-[#7a8a99] hover:text-[#b8893d] opacity-0 group-hover:opacity-100"><Link2 className="w-3 h-3" /></button>
  </li>
  );
};

export const Mandant360View = ({ mnr, aktionen = null }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [caseFilter, setCaseFilter] = useMerkZustand(`m360-akte-${mnr}`, '');
  const [edit, setEdit] = useState(null);
  const [ansicht, setAnsicht] = useMerkZustand(`m360-reiter-${mnr}`, 'uebersicht');
  const [tick, setTick] = useState(0);
  const [err, setErr] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [verbund, setVerbund] = useMerkZustand(`m360-verbund-${mnr}`, false);
  const [vorlage, setVorlage] = useState(null);

  const [params, setParams] = useSearchParams();
  const aktion = params.get('aktion') || '';
  const aktionT = params.get('t') || '';
  const geladen = !!data;
  useEffect(() => {
    if (!aktion || !geladen) return undefined;
    if (aktion === 'zeitstrahl') setAnsicht('zeitstrahl');
    if (aktion === 'zahlungslink') setLinkOpen(true);
    if (aktion === 'bearbeiten' || aktion === 'nummer') {
      setAnsicht('stammdaten');
      setTimeout(() => document.querySelector(`[data-testid="${aktion === 'nummer' ? 'm360-nummer' : 'm360-stammdaten'}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 350);
    }
    const t = setTimeout(() => setParams((p) => { const n = new URLSearchParams(p); n.delete('aktion'); n.delete('t'); return n; }, { replace: true }), 600);
    return () => clearTimeout(t);
  }, [aktion, aktionT, geladen, setAnsicht, setParams]);
  const load = useCallback(() => {
    setErr('');
    api.get(`/register/mandanten/${mnr}/360`, { params: caseFilter ? { case_id: caseFilter } : verbund ? { verbund: true } : {} }).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'Laden fehlgeschlagen'));
  }, [mnr, caseFilter, verbund]);
  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => { const f = () => { load(); setTick((x) => x + 1); }; window.addEventListener('ablage-fertig', f); return () => window.removeEventListener('ablage-fertig', f); }, [load]);

  if (err) return <p className="p-6 text-sm text-red-700" data-testid="m360-error">{err}</p>;
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#b8893d]" /></div>;
  const { mandant, akten, konten, nutzer, chats, bereiche, zeitstrahl = [] } = data;
  const gruppe = (keys) => keys.filter((k) => bereiche[k]?.items?.length).map((k) => [k, bereiche[k]]);
  const kontakt = gruppe(['termine', 'rueckrufe', 'website', 'emails', 'notfall']);
  const finanzBereiche = gruppe(['sepa', 'honorar']);
  const dokBereiche = ['dokumente', 'signaturen'].filter((k) => bereiche[k]).map((k) => [k, bereiche[k]]);
  const n = (keys) => keys.reduce((s, k) => s + (bereiche[k]?.items?.length || 0), 0);
  const ans = REITER.some(([k]) => k === ansicht) ? ansicht : 'uebersicht';
  const akteGewaehlt = akten.find((a) => a.case_id === caseFilter);
  const neuLaden = () => { load(); setTick((x) => x + 1); window.dispatchEvent(new Event('stammdaten-geaendert')); };
  const verbundGeaendert = () => { neuLaden(); window.dispatchEvent(new Event('verbund-geaendert')); };
  const bereichListe = (liste) => liste.map(([area, b]) => (
    <Block key={area} title={b.label} count={b.items.length} testId={`m360-bereich-${area}`} id={`m360-sek-${area}`}>
      {area === 'signaturen' && <Link to={`/kanzlei/mandatsunterlagen?neu=1&mnr=${mandant.mandanten_nr}`} className="inline-flex items-center gap-1 mb-2 h-8 px-3 rounded-lg text-xs bg-[#b8893d] text-white hover:bg-[#a5782f]" data-testid="m360-sig-anfordern">+ Unterschrift anfordern</Link>}
      {b.items.length ? <ul>{b.items.map((it) => <ItemRow key={it.id} it={it} onEdit={setEdit} mnr={mandant.mandanten_nr} onDone={load} />)}</ul> : <Leer text={`Noch keine ${b.label}.`} testId={`m360-leer-${area}`} />}
    </Block>
  ));
  const aktenBlock = (
    <Block title="Akten" icon={FolderOpen} count={akten.length} testId="m360-akten" id="m360-sek-akten">
      {akten.length === 0 ? <ErsteAkteButton mnr={mandant.mandanten_nr} variante="leer" /> : (
        <ul className="divide-y divide-gray-100">{akten.map((a) => (
          <li key={a.case_id} onClick={zeilenKlick(navigate, { to: `/kanzlei/akten/${a.case_id}` })} className={`grid grid-cols-1 sm:grid-cols-[7rem_minmax(0,1fr)_auto] sm:items-center gap-1 sm:gap-3 py-2 px-2 text-sm cursor-pointer hover:bg-[#f5f8fa] rounded-lg ${a.case_id === caseFilter ? 'ring-2 ring-[#b8893d]/50 bg-[#fbf7f0]' : ''}`} title="Akte öffnen" data-testid={`m360-akte-${a.case_id}`}>
            <span className="font-mono text-[#1B3A5C] text-[13px]"><VerbundTag nr={a.verbund_nr} />{a.aktenzeichen || 'ohne AZ'}</span>
            <span className="min-w-0"><span className="block truncate text-[#15202B] font-medium" title={a.bezeichnung}>{a.gegner ? a.bezeichnung : a.rechtsgebiet}</span>
              <span className="block text-[11px] text-[#7a8a99] truncate">{[statusLabel(a.status) + (a.archived && a.status !== 'abgeschlossen' ? ' · archiviert' : ''), a.gegner && a.rechtsgebiet, a.behoerde, a.behoerde_az && `AZ ${a.behoerde_az}`, a.bearbeiter?.name && `Bearbeiter: ${a.bearbeiter.name}`].filter(Boolean).join(' · ')}</span>
              <span className="block text-[11px] text-[#56616D]" data-testid={`m360-akte-herkunft-${a.case_id}`}>{a.anlage_info?.bezeichnung || 'Herkunft nicht belegt'}{a.anlage_info?.von ? ` · ${a.anlage_info.von}` : ''}</span>
              {a.haupt_mandanten_nr && a.haupt_mandanten_nr !== mnr && <span className="text-[10px] text-[#b8893d]" data-testid={`m360-akte-mit-${a.case_id}`}>{(a.weitere_mandanten || []).find((w) => w.mandanten_nr === mnr)?.rolle || 'Mitmandant'} · Hauptmandant M-{a.haupt_mandanten_nr}</span>}
              {a.weitere_mandanten?.length > 0 && a.haupt_mandanten_nr === mnr && <span className="text-[10px] text-[#b8893d]" data-testid={`m360-akte-weitere-${a.case_id}`}>+{a.weitere_mandanten.length} weitere Mandant(en)</span>}</span>
            <span className="flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <Link to={`/kanzlei/akten/${a.case_id}`} className="h-7 px-3 inline-flex items-center rounded-full bg-[#1B3A5C] text-white text-[11px] hover:bg-[#b8893d] transition-colors" data-testid={`m360-akte-oeffnen-${a.case_id}`}>Öffnen</Link>
              <AkteBearbeiten a={a} onSaved={load} /><AkteStatusBtn a={a} onSaved={load} /><MappeBtn akte={a} />
            </span>
          </li>))}</ul>
      )}
    </Block>
  );

  return (
    <div className="space-y-3" data-testid="m360-view">
      <KopfKarte mandant={mandant} akten={akten} verbund={data.verbund || []} verbundAktiv={!!data.verbund_aktiv} onVerbund={() => { setCaseFilter(''); setVerbund((v) => !v); }} onReiter={setAnsicht} />
      <AnlageHerkunft mnr={mandant.mandanten_nr} compact />
      {aktionen}
      <VerbundVorschlaege key={`vv-${tick}`} mnr={mandant.mandanten_nr} onVerknuepft={verbundGeaendert} />
      {data.verbund_aktiv && <p className="text-xs text-[#b8893d] px-1" data-testid="m360-verbund-hinweis">Zusammenansicht aller verknüpften Nummern – jede Zeile zeigt ihre Mandantennummer. Rechnungen und Zahlungen bleiben je Nummer getrennt.</p>}

      <Reiter an={ans} setAn={setAnsicht} zahlen={{ akten: akten.length, dokumente: n(['dokumente', 'signaturen']), zeitstrahl: zeitstrahl.length }} />
      {akten.length > 1 && !['stammdaten', 'zugriffe'].includes(ans) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="m360-akte-filter">
          <span className="text-[11px] text-[#7a8a99] mr-1">Anzeigen für:</span>
          {[{ case_id: '', label: 'Alle Akten' }, ...akten.map((a) => ({ case_id: a.case_id, label: a.aktenzeichen ? `AZ ${a.aktenzeichen}` : a.rechtsgebiet, archived: a.archived }))].map((f) => (
            <button key={f.case_id || 'all'} type="button" onClick={() => setCaseFilter(f.case_id)} aria-pressed={caseFilter === f.case_id} data-testid={`m360-filter-${f.case_id || 'all'}`}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${caseFilter === f.case_id ? 'bg-[#1B3A5C] border-[#1B3A5C] text-white' : 'bg-white border-gray-200 text-[#1B3A5C] hover:border-[#b8893d]'} ${f.archived ? 'opacity-60' : ''}`}>{f.label}{f.archived ? ' · archiviert' : ''}</button>
          ))}
        </div>)}

      <div data-testid={`m360-inhalt-${ans}`} className="space-y-3">
        {ans === 'uebersicht' && (
          <div className="grid xl:grid-cols-[minmax(0,1fr)_420px] gap-3 items-start" data-testid="m360-gesamt">
            <div className="space-y-3 min-w-0">{aktenBlock}{kontakt.length ? bereichListe(kontakt) : <Block title="Termine, Rückrufe & Anfragen" testId="m360-kontakt-leer"><Leer text="Keine Termine, Rückrufe oder Anfragen." /></Block>}</div>
            <div className="space-y-3 min-w-0">
              {akteGewaehlt ? <AkteExtras akte={akteGewaehlt} mnr={mandant.mandanten_nr} onSaved={load} /> : <Block title="Notizen" testId="m360-randnotizen" id="m360-sek-notizen"><Randnotizen mnr={mandant.mandanten_nr} cid="" /></Block>}
            </div>
          </div>)}
        {ans === 'akten' && aktenBlock}
        {ans === 'nachrichten' && <div id="m360-sek-chat" className="grid"><MandantChat key={`chat-${caseFilter}`} chats={chats} chatEnabled={mandant.chat_enabled} mandant={mandant} /></div>}
        {ans === 'dokumente' && (<>
          <AblageStreifen ziel={`M-${mandant.mandanten_nr}${akteGewaehlt ? ` · ${akteGewaehlt.aktenzeichen ? `AZ ${akteGewaehlt.aktenzeichen}` : 'gewählte Akte'}` : ''}`} testId="m360-ablage" />
          <div className="grid xl:grid-cols-2 gap-3 items-start">{bereichListe(dokBereiche)}</div>
        </>)}
        {ans === 'finanzen' && (
          <div className="grid xl:grid-cols-2 gap-3 items-start" data-testid="m360-finanzen">
            <div className="space-y-3 min-w-0" id="m360-sek-zahlungen">
              <ZahlungenKarte zahlungen={data.zahlungen} mnr={mandant.mandanten_nr} caseId={caseFilter} />
              {bereichListe(finanzBereiche)}
            </div>
            <div className="space-y-3 min-w-0">
              <RechnungenKarte mnr={mandant.mandanten_nr} caseId={caseFilter} />
              <HonorarKarte mnr={mandant.mandanten_nr} caseId={caseFilter} akten={akten} onAkonto={(h) => { setVorlage({ betrag: Number(h.brutto).toFixed(2).replace('.', ','), zweck: 'Vorschuss gemäß Honorarvorschlag', case_id: h.case_id || '' }); setLinkOpen(true); }} />
              <AkontoOhneRechnung mnr={mandant.mandanten_nr} kompakt />
            </div>
          </div>)}
        {ans === 'stammdaten' && (
          <div className="grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 items-start" data-testid="m360-stammdaten-reiter">
            <div className="space-y-3 min-w-0">
              <div id="m360-sek-stammdaten"><StammdatenKarte mandant={mandant} akte={akten[0]} onSaved={neuLaden} aktion={aktion} /></div>
              <Block title="Portalzugang" icon={Users} count={konten.length + nutzer.filter((x) => x.status === 'aktiv').length} testId="m360-nutzer" id="m360-sek-nutzer">
                <NutzerPanel mnr={mandant.mandanten_nr} konten={konten} nutzer={nutzer} akten={akten} onChanged={load} />
              </Block>
            </div>
            <div className="space-y-3 min-w-0" id="m360-sek-erfassung">
              <ErweiterteStammdaten key={`erw-${tick}-${caseFilter}`} mnr={mandant.mandanten_nr} caseId={caseFilter} />
              <Stammblatt mnr={mandant.mandanten_nr} tick={tick} onVerbund={verbundGeaendert} />
            </div>
          </div>)}
        {ans === 'zeitstrahl' && <Zeitstrahl events={zeitstrahl} mnr={mandant.mandanten_nr} caseId={caseFilter} onStammblatt={() => setAnsicht('stammdaten')} />}
        {ans === 'zugriffe' && <Zugriffe mnr={mandant.mandanten_nr} />}
      </div>

      {edit && <ZuordnenDialog item={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
      {linkOpen && <AkontoLinkDialog mandant={mandant} akten={akten.filter((a) => !a.archived)} caseId={caseFilter} vorlage={vorlage} onClose={() => { setLinkOpen(false); setVorlage(null); }} onDone={load} />}
    </div>
  );
};

export default Mandant360View;
